import { useEffect, useState } from "react";
import {
  LogOut, Store, Activity, TrendingUp, Users, ShieldCheck, LayoutDashboard, RefreshCw, CreditCard, Settings, Trash2, Inbox, History,
} from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtNum } from "@/lib/format";
import { LangSwitch } from "@/components/LangSwitch";
import { Stat } from "@/components/ui-kit";
import { Logo, Wordmark } from "@/components/Logo";
import ShopPlanDialog from "@/components/admin/ShopPlanDialog";
import ShopControlDialog from "@/components/admin/ShopControlDialog";
import { PlatformSettings, RequestsPanel } from "@/components/admin/AdminSaas";
import { ApprovalsPanel, InvoicesPanel } from "@/components/admin/AdminAccounts";
import UsersTab from "@/components/admin/UsersTab";
import AdminsTab from "@/components/admin/AdminsTab";
import ActivityTab from "@/components/admin/ActivityTab";
import RemoteAppTab from "@/components/admin/RemoteAppTab";
import { MonitorDown } from "lucide-react";
import { STATUS_TONE } from "@/lib/plans";

const TABS = [
  { key: "overview", icon: LayoutDashboard },
  { key: "stores", icon: Store, perm: "stores" },
  { key: "users", icon: Users, perm: "users" },
  { key: "billing", icon: CreditCard, perm: "billing" },
  { key: "admins", icon: ShieldCheck, perm: "super" },
  { key: "activity", icon: History, perm: "super" },
  { key: "remoteapp", icon: MonitorDown, perm: "super" },
  { key: "settings", icon: Settings, perm: "settings" },
];

function Overview({ data }) {
  const { t } = useApp();
  const chart = data.shops.map((s) => ({ n: s.name.slice(0, 14), v: s.sales_30d }));
  return (
    <div className="space-y-5" data-testid="admin-overview-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={t("stores")} value={data.totals.shops} icon={Store} tone="#F0444B" testid="admin-total-shops" />
        <Stat label={t("active_30d")} value={data.totals.active_30d} icon={Activity} tone="#1F9D55" testid="admin-active-shops" />
        <Stat label={t("sales_30d")} value={fmtNum(data.totals.sales_30d)} icon={TrendingUp} tone="#E67E22" testid="admin-sales-30d" />
        <Stat label={t("pending_requests")} value={data.totals.pending_requests} icon={Inbox} tone="#E74C3C" testid="admin-pending-requests" />
        <Stat label={t("acct_requests")} value={data.totals.pending_approvals || 0} icon={ShieldCheck} tone="#E67E22" testid="admin-pending-approvals" />
        <Stat label={t("inv_claimed_count")} value={data.totals.claimed_invoices || 0} icon={CreditCard} tone="#2B79C2" testid="admin-claimed-invoices" />
      </div>
      <div className="rounded-xl bg-white border border-border p-5 card-soft" data-testid="admin-sales-chart">
        <h3 className="font-heading text-lg mb-3">{t("sales_30d")}</h3>
        <div className="h-56" dir="ltr">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart}><XAxis dataKey="n" fontSize={11} tickLine={false} axisLine={false} /><YAxis fontSize={11} width={56} tickLine={false} axisLine={false} /><Tooltip formatter={(v) => fmtNum(v)} cursor={{ fill: "#F2F2EE" }} /><Bar dataKey="v" fill="#0B2A5B" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function StoresTab({ data, reload }) {
  const { t, lang } = useApp();
  const [manage, setManage] = useState(null);
  const [control, setControl] = useState(null);
  const remove = async (s) => {
    if (!window.confirm(t("delete_store_confirm", { n: s.name }))) return;
    try { await api.delete(`/admin/shops/${s.id}`); toast.success(t("store_deleted")); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-xl bg-white border border-border card-soft" data-testid="admin-stores-tab">
      <table className="w-full text-xs sm:text-sm table-fixed" data-testid="admin-shops-table">
        <colgroup><col className="w-[17%]" /><col className="w-[18%]" /><col className="w-[12%]" /><col className="w-[12%]" /><col className="w-[11%]" /><col className="w-[12%]" /><col className="w-[18%]" /></colgroup>
        <thead className="text-muted-foreground"><tr>{["shop_name", "email", "plan", "ends", "counts", "sales_30d", ""].map((k, i) => <th key={i} className="px-2 py-2 text-start font-medium">{k === "counts" ? `${t("products")} · ${t("staff")} · ${t("limit_devices")}` : k && t(k)}</th>)}</tr></thead>
        <tbody>
          {data.shops.map((s) => (
            <tr key={s.id} className="border-t border-border hover:bg-secondary/50 align-top" data-testid="admin-shop-row">
              <td className="px-2 py-2 font-semibold break-words">{s.name}{s.approval && s.approval !== "approved" && <span className="block mt-1 px-2 py-0.5 rounded-full text-[11px] font-bold text-white w-fit" style={{ background: s.approval === "pending" ? "#E67E22" : "#C7353B" }} data-testid="admin-shop-approval">{t(`acct_${s.approval}`)}</span>}</td>
              <td className="px-2 py-2 break-all text-muted-foreground" dir="ltr">{s.owner_email}</td>
              <td className="px-2 py-2"><span className="block font-bold" data-testid="admin-shop-plan">{t(`plan_${s.effective}`)}</span><span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-bold text-white" style={{ background: STATUS_TONE[s.status] }} data-testid="admin-shop-status">{t(`plan_status_${s.status}`)}</span></td>
              <td className="px-2 py-2 text-muted-foreground">{fmtDate(s.status === "active" ? s.plan_expires : s.trial_ends, lang)}<span className="block text-[11px] mt-1">{t("last_sync")}: {fmtDate(s.last_sync_at, lang)}</span></td>
              <td className="px-2 py-2 tabular-nums" dir="ltr"><span data-testid="admin-shop-counts">{s.products} · {s.staff} · {s.devices}</span></td>
              <td className="px-2 py-2 font-semibold tabular-nums">{fmtNum(s.sales_30d)} <span className="text-[11px] text-muted-foreground">{s.currency}</span></td>
              <td className="px-2 py-2"><span className="flex flex-wrap gap-1">
                <button type="button" onClick={() => setControl(s)} className="h-8 px-2 rounded-lg border border-[#F0444B] text-[#F0444B] text-[11px] font-bold" data-testid="admin-control-btn"><ShieldCheck className="w-3.5 h-3.5 inline me-1" />{t("admin_control")}</button>
                <button type="button" onClick={() => setManage(s)} className="h-8 px-2 rounded-lg bg-[#0B2A5B] text-white text-[11px] font-bold" data-testid="admin-manage-btn">{t("manage")}</button>
                <button type="button" onClick={() => remove(s)} aria-label={t("delete_store")} className="h-8 w-8 rounded-lg bg-destructive/10 text-destructive grid place-items-center" data-testid="admin-delete-store-btn"><Trash2 className="w-4 h-4" /></button>
              </span></td>
            </tr>
          ))}
        </tbody>
      </table>
      <ShopPlanDialog shop={manage} onClose={() => setManage(null)} onChanged={() => { setManage(null); reload(); }} />
      {control && <ShopControlDialog shop={control} onClose={() => setControl(null)} onChanged={reload} />}
    </div>
  );
}

export default function Admin() {
  const { t, logout, user } = useApp();
  const can = (p) => !p || user.role === "superadmin" || (p !== "super" && (user.admin_perms || []).includes(p));
  const tabs = TABS.filter((x) => can(x.perm));
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get("/admin/stats").then((r) => setData(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, []);
  const TabBtn = ({ x, mobile }) => (
    <button type="button" onClick={() => setTab(x.key)} data-testid={`admin-tab-${x.key}${mobile ? "-m" : ""}`}
      className={mobile ? `shrink-0 h-10 px-3 rounded-full text-sm font-bold flex items-center gap-1.5 ${tab === x.key ? "bg-[#0B2A5B] text-white" : "bg-white border border-border"}`
        : `w-full flex items-center gap-3 h-[50px] px-5 border-s-4 font-medium transition-colors ${tab === x.key ? "border-[#F0444B] bg-white/10" : "border-transparent text-white/70 hover:bg-white/5"}`}>
      <x.icon className="w-5 h-5" /><span className="flex-1 text-start">{t(`tab_${x.key}`)}</span>
      {x.key === "billing" && (data?.totals.pending_requests + data?.totals.pending_approvals + data?.totals.claimed_invoices) > 0 && <span className="min-w-6 h-6 px-1.5 rounded-full text-xs font-bold grid place-items-center bg-[#E74C3C] text-white" data-testid="admin-billing-badge">{data.totals.pending_requests + (data.totals.pending_approvals || 0) + (data.totals.claimed_invoices || 0)}</span>}
    </button>
  );
  return (
    <div className="min-h-screen lg:ps-64" data-testid="super-admin-analytics-view">
      <aside className="hidden lg:flex fixed inset-y-0 start-0 w-64 bg-[#0B2A5B] text-white flex-col z-40">
        <div className="h-16 px-5 flex items-center gap-3 border-b border-white/10"><Logo className="w-9 h-9" /><Wordmark size="sm" /></div>
        <div className="px-5 py-4 text-xs uppercase tracking-wider text-white/50">{t("admin_title")}</div>
        <nav className="flex-1">{tabs.map((x) => <TabBtn key={x.key} x={x} />)}</nav>
      </aside>
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <Logo className="w-9 h-9 lg:hidden" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-muted-foreground truncate" data-testid="admin-welcome">{t("welcome")}, {user.name} · {t(`role_${user.role}`)}</div>
            <div className="font-heading text-lg truncate">{t(`tab_${tab}`)}</div>
          </div>
          <button type="button" onClick={load} className="h-10 w-10 rounded-full border border-border grid place-items-center" aria-label={t("sync_now")} data-testid="admin-refresh-btn"><RefreshCw className="w-4 h-4" /></button>
          <LangSwitch compact />
          <button type="button" onClick={logout} className="h-10 px-4 rounded-full border border-foreground/80 flex items-center gap-1.5 font-semibold text-sm" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" /></button>
        </div>
        <div className="lg:hidden flex flex-nowrap gap-2 overflow-x-auto px-4 pb-2">{tabs.map((x) => <TabBtn key={x.key} x={x} mobile />)}</div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {err && <p className="text-destructive font-bold">{err}</p>}
        {!data && !err && <p>{t("loading")}</p>}
        {data && tab === "overview" && <Overview data={data} />}
        {data && tab === "stores" && <StoresTab data={data} reload={load} />}
        {tab === "users" && <UsersTab />}
        {tab === "billing" && <><ApprovalsPanel onChanged={load} /><InvoicesPanel onChanged={load} /><RequestsPanel onChanged={load} /></>}
        {tab === "admins" && <AdminsTab />}
        {tab === "activity" && <ActivityTab />}
        {tab === "remoteapp" && <RemoteAppTab />}
        {tab === "settings" && <PlatformSettings />}
      </main>
    </div>
  );
}
